import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { ActivityIndicator, Platform, Pressable, TextInput, View } from 'react-native';
import { Control, useController } from 'react-hook-form';
import MaskInput from 'react-native-mask-input';

import { ControlledInput } from '@/components/forms/controlled-input';
import { ThemedText } from '@/components/themed-text';
import { useProviderProfile } from '@/hooks/use-provider';
import { ServiceFormData } from '@/lib/validators/service';
import {
  CepAddress,
  composeAddress,
  fetchAddressByCep,
  isValidCep,
} from '@/services/cepService';
import { cn } from '@/utils/cn';
import { getApiErrorMessage } from '@/utils/api-error';

const CEP_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/];
const MUTED = '#A3A3A3';

const webInputReset = Platform.OS === 'web' ? ({ outline: 'none' } as never) : undefined;

/**
 * Local fixo do serviço. Duas saídas rápidas em vez de um campo livre em branco:
 * herdar o endereço do perfil, ou buscar por CEP e completar só o número.
 * O campo de texto continua editável — CEP e perfil são atalhos, não trilhos.
 */
export function ServiceAddressField({ control }: { control: Control<ServiceFormData> }) {
  const { field } = useController({ control, name: 'address' });
  const { data: provider } = useProviderProfile();
  const profileAddress = provider?.address?.trim() || '';

  const [cep, setCep] = useState('');
  const [base, setBase] = useState<CepAddress | null>(null);
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);

  const usingProfileAddress = !!profileAddress && field.value?.trim() === profileAddress;

  const applyProfileAddress = () => {
    setBase(null);
    setCepError(null);
    field.onChange(profileAddress);
  };

  const applyComposed = (next: {
    base: CepAddress;
    number?: string;
    complement?: string;
  }) => {
    field.onChange(composeAddress(next));
  };

  const handleSearchCep = async () => {
    if (!isValidCep(cep)) {
      setCepError('Informe um CEP com 8 dígitos.');
      return;
    }

    setIsSearching(true);
    setCepError(null);
    try {
      const found = await fetchAddressByCep(cep);
      setBase(found);
      applyComposed({ base: found, number, complement });
    } catch (error) {
      setBase(null);
      setCepError(getApiErrorMessage(error, 'Não foi possível buscar o CEP.'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleNumberChange = (value: string) => {
    setNumber(value);
    if (base) applyComposed({ base, number: value, complement });
  };

  const handleComplementChange = (value: string) => {
    setComplement(value);
    if (base) applyComposed({ base, number, complement: value });
  };

  return (
    <View className="gap-3">
      {profileAddress ? (
        <Pressable
          onPress={applyProfileAddress}
          className={cn(
            'flex-row items-center gap-2 rounded-2xl border px-3 py-2.5',
            usingProfileAddress
              ? 'border-primary bg-primary/5'
              : 'border-neutral-200 bg-neutral-50',
          )}
        >
          <Ionicons
            name={usingProfileAddress ? 'checkmark-circle' : 'home-outline'}
            size={16}
            color={usingProfileAddress ? '#FD5001' : MUTED}
          />
          <View className="flex-1">
            <ThemedText className="text-xs font-semibold text-foreground">
              Usar o endereço do perfil
            </ThemedText>
            <ThemedText numberOfLines={1} className="text-xs text-neutral-500">
              {profileAddress}
            </ThemedText>
          </View>
        </Pressable>
      ) : null}

      <View className="gap-2">
        <ThemedText className="text-sm font-medium">Buscar por CEP</ThemedText>
        <View className="flex-row items-center gap-2">
          <MaskInput
            value={cep}
            onChangeText={(masked) => {
              setCep(masked);
              setCepError(null);
            }}
            mask={CEP_MASK}
            keyboardType="number-pad"
            placeholder="00000-000"
            placeholderTextColor={MUTED}
            style={webInputReset}
            className="h-11 flex-1 rounded-full bg-neutral-100 px-4 text-sm text-foreground"
          />
          <Pressable
            onPress={handleSearchCep}
            disabled={isSearching}
            className="h-11 flex-row items-center gap-1.5 rounded-full bg-primary px-4 active:opacity-80 disabled:opacity-60"
          >
            {isSearching ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Ionicons name="search" size={15} color="#fff" />
            )}
            <ThemedText className="text-sm font-semibold text-white">Buscar</ThemedText>
          </Pressable>
        </View>
        {cepError ? (
          <ThemedText className="text-xs text-error-600">{cepError}</ThemedText>
        ) : null}
      </View>

      {base ? (
        <View className="flex-row gap-2">
          <View className="w-28 gap-2">
            <ThemedText className="text-sm font-medium">Número</ThemedText>
            <TextInput
              value={number}
              onChangeText={handleNumberChange}
              keyboardType="number-pad"
              placeholder="123"
              placeholderTextColor={MUTED}
              style={webInputReset}
              className="h-11 rounded-full bg-neutral-100 px-4 text-sm text-foreground"
            />
          </View>
          <View className="flex-1 gap-2">
            <ThemedText className="text-sm font-medium">Complemento</ThemedText>
            <TextInput
              value={complement}
              onChangeText={handleComplementChange}
              placeholder="Sala 2, bloco B..."
              placeholderTextColor={MUTED}
              style={webInputReset}
              className="h-11 rounded-full bg-neutral-100 px-4 text-sm text-foreground"
            />
          </View>
        </View>
      ) : null}

      <ControlledInput
        control={control}
        name="address"
        label="Endereço"
        placeholder="Endereço onde o serviço acontece"
        helperText="É o que o cliente vê ao agendar. Dá para ajustar à mão."
      />
    </View>
  );
}
